import React from 'react';
import type { CityPreview } from '../../data/featuredCityPreviews';
import { getCityImageSources } from '../../data/cityPhotos';
import { FEATURED_CITY_PREVIEWS } from '../../data/featuredCityPreviews';
import { SkeletonValue } from './SkeletonValue';

type CityCardSkeletonProps = {
  city: CityPreview;
};

const CityCardSkeleton: React.FC<CityCardSkeletonProps> = ({ city }) => {
  const { src, srcSet, imagePosition } = getCityImageSources(city.id, undefined, city.imagePosition);
  const cityShort = city.name.split(' ')[0];

  return (
    <article
      className="flex h-full min-h-[200px] flex-col overflow-hidden rounded-3xl border border-[rgba(74,33,71,0.1)] bg-white sm:min-h-[220px] sm:flex-row"
      aria-hidden
    >
      <div className="relative aspect-[16/10] w-full shrink-0 bg-gray-100 sm:aspect-auto sm:min-h-[220px] sm:w-[42%] sm:min-w-[200px] sm:max-w-[300px]">
        <img
          src={src}
          srcSet={srcSet}
          sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 300px"
          alt=""
          loading="eager"
          decoding="async"
          style={{ objectPosition: imagePosition }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-between p-6 sm:p-7">
        <header className="space-y-1">
          <h3 className="font-jakarta text-2xl font-bold leading-tight tracking-tight text-[#2a0f2a] sm:text-[1.65rem]">
            {city.name}
          </h3>
          <p className="pt-0.5 font-jakarta text-sm leading-snug text-[#5a3158]">{city.tagline}</p>
        </header>

        <div className="mt-5 grid grid-cols-3 gap-0 overflow-hidden rounded-2xl border border-[rgba(74,33,71,0.1)] bg-chisme-bg sm:mt-6">
          {(['Planes', 'Chicas', 'Hoy'] as const).map((label, i) => (
            <div
              key={label}
              className={`px-3 py-3.5 text-center sm:px-4 sm:py-4 ${
                i < 2 ? 'border-r border-[rgba(74,33,71,0.1)]' : ''
              }`}
            >
              <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                {label}
              </p>
              <div className="mt-1 flex justify-center">
                <SkeletonValue
                  className={`h-6 sm:h-7 ${
                    label === 'Chicas' ? 'w-12 sm:w-14' : label === 'Hoy' ? 'w-8' : 'w-10'
                  }`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3 border-t border-[rgba(74,33,71,0.1)] pt-4">
          <span className="font-jakarta text-sm font-semibold text-chisme-primary">
            Ver planes en {cityShort}
          </span>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[rgba(74,33,71,0.1)] text-chisme-primary">
            <svg className="-mr-px h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </article>
  );
};

type CityCardSkeletonGridProps = {
  cities?: CityPreview[];
};

export function CityCardSkeletonGrid({ cities = FEATURED_CITY_PREVIEWS }: CityCardSkeletonGridProps) {
  return (
    <div
      className="grid grid-cols-1 gap-6 md:gap-7 lg:grid-cols-2"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Cargando ciudades"
    >
      {cities.map((city) => (
        <CityCardSkeleton key={city.id} city={city} />
      ))}
      <span className="sr-only">Cargando ciudades…</span>
    </div>
  );
}

export default CityCardSkeleton;
